import { setGlobalEventHandler } from "@/hooks/useRealTimeUpdates";
import { formatRelativeTime } from "@/shared/lib/format";
import { getStatusVariant } from "@/shared/lib/status";
import { useUIStore } from "@/shared/stores/ui-store";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Circle, Clock, X } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface ActivityLogProps {
  onClose?: () => void;
}

export function ActivityLog({ onClose }: ActivityLogProps) {
  const navigate = useNavigate();
  const {
    activityLogEvents,
    activityLogFilter,
    addActivityLogEvent,
    setActivityLogFilter,
    selectedActivityEventId,
    setSelectedActivityEventId,
  } = useUIStore();

  const handleEventClick = (event: { type: string; objectId: string }) => {
    if (!event.objectId) return;

    switch (event.type) {
      case "account":
        navigate(`/accounts?id=${event.objectId}`);
        break;
      case "application":
        navigate(`/applications?id=${event.objectId}`);
        break;
      case "transaction":
        navigate(`/transactions?id=${event.objectId}`);
        break;
      default:
        // For webhook, location, config - no navigation
        return;
    }
  };

  useEffect(() => {
    setGlobalEventHandler(addActivityLogEvent);
  }, [addActivityLogEvent]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-event-card]") === null) {
        setSelectedActivityEventId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [setSelectedActivityEventId]);

  const filteredEvents =
    activityLogFilter === "all"
      ? activityLogEvents
      : activityLogEvents.filter((event) => event.type === activityLogFilter);

  return (
    <div className="flex h-full flex-col gap-4 bg-card p-4">
      <div className="flex items-center justify-between px-4">
        <h2 className="text-[20px] font-medium leading-[28px] tracking-[-0.6px] text-black">
          Activity Log
        </h2>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="h-4 w-4">
            <X className="h-1 w-1 text-gray-500" />
          </Button>
        )}
      </div>

      <div>
        <Select value={activityLogFilter} onValueChange={setActivityLogFilter}>
          <SelectTrigger width="full">
            <SelectValue placeholder="All Objects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="account">Accounts</SelectItem>
            <SelectItem value="application">Applications</SelectItem>
            <SelectItem value="transaction">Transactions</SelectItem>
            <SelectItem value="webhook">Webhooks</SelectItem>
            <SelectItem value="location">Locations</SelectItem>
            <SelectItem value="config">Config</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="flex-1">
        <div>
          {filteredEvents.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Circle className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>No events yet</p>
              <p className="text-sm">Real-time events will appear here</p>
            </div>
          ) : (
            <div>
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  data-event-card
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedActivityEventId(event.id);
                    // Navigate to detail view if type supports navigation
                    if (
                      event.type === "account" ||
                      event.type === "application" ||
                      event.type === "transaction"
                    ) {
                      handleEventClick(event);
                    }
                  }}
                  className={`flex cursor-pointer items-center justify-between border-b border-gray-400 px-[12px] py-[8px] transition-colors hover:bg-gray-100 ${
                    selectedActivityEventId === event.id ? "bg-blue-100" : ""
                  }`}
                >
                  <div className="flex flex-1 flex-col gap-[4px]">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 shrink-0 text-gray-400" />
                      <p className="text-[14px] font-medium leading-[28px] tracking-[-0.42px] text-gray-400">
                        {formatRelativeTime(event.timestamp)}
                      </p>
                    </div>
                    <p className="text-[16px] font-medium leading-[24px] tracking-[-0.48px] text-gray-950">
                      {event.description}
                    </p>
                    <p className="text-[14px] font-medium leading-[28px] tracking-[-0.42px] text-gray-500">
                      {event.externalId}
                    </p>
                  </div>
                  <Badge
                    variant={getStatusVariant(event.status)}
                    className="h-fit min-w-[80px] shrink-0 justify-center px-[6px] py-[1px]"
                  >
                    {event.status === "info"
                      ? "Info"
                      : event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
